import { inject, Injectable, Injector, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, of, shareReplay, switchMap } from 'rxjs';
import { take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth.service';

export interface UserFriend {
  friend_id: string;
  friend_username: string;
  friend_avatar_url: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UserFriendsViewService {
  private readonly _auth = inject(AuthService);
  private readonly _http = inject(HttpClient);
  private readonly _injector = inject(Injector);

  // null until the first fetch resolves, matching the previous "no subject yet" guard.
  private readonly _state = signal<UserFriend[] | null>(null);

  state$ = this._auth.verifiedUser$.pipe(
    switchMap((user) => {
      if (!user) {
        return of([]);
      }

      return this._http
        .get<UserFriend[]>(`${environment.apiUrl}/self/friends`)
        .pipe(take(1))
        .pipe(
          switchMap((userFriends) => {
            this._state.set(userFriends);
            // Re-source the live state from the signal so addFriend/removeFriend
            // re-emit through this stream, as the recreated BehaviorSubject did.
            return toObservable(this._state, {
              injector: this._injector,
            }).pipe(map((friends) => friends ?? []));
          }),
          // Inner, so a failed fetch degrades to an empty list for this user
          // instead of completing the shared stream.
          catchError(() => of([] as UserFriend[])),
        );
    }),
    shareReplay(1),
  );

  stateByUserId$ = this.state$.pipe(
    map((friends) =>
      friends.reduce(
        (acc, f) => ({
          ...acc,
          [f.friend_id]: f,
        }),
        {} as Record<string, UserFriend>,
      ),
    ),
    shareReplay(1),
  );

  get state() {
    return this._state() ?? [];
  }

  addFriend(friend: UserFriend) {
    if (this._state() === null) {
      return;
    }
    this._state.set([...this.state, friend]);
  }

  removeFriend(user_id: string) {
    if (this._state() === null) {
      return;
    }
    this._state.set(this.state.filter((f) => f.friend_id !== user_id));
  }
}
